import { Component, HostListener, TemplateRef, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { GroupDescriptor, SortDescriptor, State, process } from '@progress/kendo-data-query';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { whitespace } from '../common/whitespace';
import { LoaderService } from '../loader/loader.service';
import { ClientOfferService } from '../client-offer/client-offer.service';
import { ToastrService } from 'ngx-toastr';
import { MapService } from '../service/map.service';
import { ApiService } from '../service/api.service';
import { GridSettingsService } from '../service/grid-settings.service';

@Component({
  selector: 'app-client-offer',
  templateUrl: './client-offer.component.html',
  styleUrls: ['./client-offer.component.css']
})
export class ClientOfferComponent implements OnInit, AfterViewInit {
  @ViewChild("grid", { static: true }) gridKendo: GridComponent;
  @ViewChild("clientOfferDialog") clientOfferDialog: TemplateRef<any>;
  @ViewChild("sendmailAgainDialog") sendmailAgainDialog: TemplateRef<any>;
  public groups: GroupDescriptor[] = [];
  public gridHeight: number;
  searchString: any = '';
  columnList: any = [];
  clientOfferData: any;
  gridView: GridDataResult;
  kendoLoading = false;
  total: number = 0;
  state: State = {
    skip: 0,
    take: 10,
    filter: {
      logic: "or",
      filters: [],
    },
    sort: [
      {
        dir: "asc",
        field: "",
      },
    ],
    group: this.groups,
  };
  public pageableWithAll: any = {
    pageSizes: [5, 10, 20, 50, 100, 'All']
  };
  public sort: SortDescriptor[] = [];
  addClientOfferFrom!: FormGroup;
  isSubmit = false;
  editTypeId: any;
  selectedRecord: any;
  action: any;
  planOptions = [
    { label: 'Basic', value: 'Basic' },
    { label: 'Standard', value: 'Standard' },
    { label: 'Premium', value: 'Premium' },
    { label: 'Enterprise', value: 'Enterprise' }
  ];
  isMaximized = false;
  originalOverlayStyle: Partial<CSSStyleDeclaration> = {};
  originalDialogStyle: Partial<CSSStyleDeclaration> = {};
  TableName = "ClientOffer";
  constructor(
    private readonly dialog: MatDialog,
    private readonly FormBuilder: FormBuilder,
    private readonly clientOfferService: ClientOfferService,
    private readonly loaderService: LoaderService,
    private readonly toaster: ToastrService,
    private readonly mapService: MapService,
    private readonly api: ApiService,
    private readonly gridSettings: GridSettingsService
  ) {

  }

  ngOnInit(): void {
    this.loadClientOfferData();
    this.setGridHeight();
    this.LoadForm();
  }

  LoadForm() {
    this.addClientOfferFrom = this.FormBuilder.group({
      Name: ['', [Validators.required, whitespace]],
      PlanName: ['', [Validators.required, whitespace]],
      Email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$'),
          whitespace,
        ],
      ],
      Price: ['', [Validators.required, whitespace]],
      Notes: [''],
      PlanType: ['Month', Validators.required],
      PlanDuration: [1, [Validators.required, Validators.min(1)]]
    });
  }
  filterData(event: any) {
    this.searchString = event;

    const filtered = this.clientOfferData.filter((item: any) =>
      Object.values(item).some(val =>
        val?.toString().toLowerCase().includes(this.searchString.toLowerCase())
      )
    );
    this.gridView = process(filtered, this.state);
  }

  updateKendoGridData() {
    this.gridView = process(this.clientOfferData, this.state);
  }

  public pageChange(event: PageChangeEvent): void {
    this.state.skip = event.skip;
    this.updateKendoGridData();
  }

  clearList() {
    this.searchString = '';
    this.updateKendoGridData();
  }
  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.updateKendoGridData();
  }

  public dataStateChange(state: any): void {
    this.state = state;
    this.updateKendoGridData();
  }

  groupChange(groups: GroupDescriptor[]): void {
    this.groups = groups;
    this.updateKendoGridData();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.setGridHeight();
  }

  setGridHeight(): void {
    const headerHeight = 70; // Adjust according to your header and other elements
    const footerHeight = 61;
    const otherElementsHeight = 125;
    this.gridHeight = window.innerHeight - headerHeight - footerHeight - otherElementsHeight;
  }

  closeDialog() {
    this.dialog?.closeAll();
  }

  openClientOfferDialog(data: any) {
    this.isMaximized = false;

    if (data) {
      const updatedData = {
        ...data,
        PlanType: data.IsMonth ? 'Month' : 'Year'
      };

      this.addClientOfferFrom.patchValue(updatedData);
      this.editTypeId = data.Id;
      this.action = "Update";
    } else {
      this.action = "Add";
      this.editTypeId = 0;
      this.setDefaultFormValues();
    }

    const dialogRef = this.dialog?.open(this.clientOfferDialog, {
      width: '40vw',
      autoFocus: false
    });

    dialogRef?.afterClosed().subscribe(() => {
      this.setDefaultFormValues();
    });
  }

  setDefaultFormValues() {
    this.addClientOfferFrom.reset({
      Name: '',
      PlanName: '',
      Email: '',
      Price: '',
      Notes: '',
      PlanType: 'Month',
      PlanDuration: 1
    });
  }

  submitClientOffer() {
    this.isSubmit = true;

    if (!this.addClientOfferFrom.valid) {
      this.addClientOfferFrom.markAllAsTouched();
      return;
    }

    const formValue = this.addClientOfferFrom.value;
    const payload = {
      Id: this.editTypeId,
      ...formValue,
      IsMonth: formValue.PlanType === 'Month'
    };

    if (this.editTypeId && this.editTypeId !== 0) {
      this.updateClientOffer(payload);
    } else {
      this.addClientOffer(payload);
    }
  }

  addClientOffer(payload: any) {
    this.loaderService.show();
    this.clientOfferService.AddClientOffer(payload).subscribe((res: any) => {
      if (res["Success"]) {
        this.loaderService.hide();
        this.toaster.success('Client Offer Added Successfully');
        this.closeDialog();
        this.loadClientOfferData();
        this.isSubmit = false;
        this.editTypeId = 0;
      } else {
        this.loaderService.hide();
        this.toaster.error(res["me"]);
      }
    })
  }

  updateClientOffer(payload: any) {
    this.loaderService.show();
    this.clientOfferService.updateClientOffer(payload).subscribe({
      next: (res: any) => {
        if (res["Success"]) {
          this.loaderService.hide();
          this.toaster.success('Client Offer Updated Successfully');
          this.closeDialog();
          this.loadClientOfferData();
          this.isSubmit = false;
          this.loadmailsendPop();
        } else {
          this.loaderService.hide();
          this.toaster.error('Error');
        }
      },
      error: (_err) => {
        this.loaderService.hide();
        this.toaster.error('Something went wrong Record not Updated sorry');
      }
    });
  }

  loadmailsendPop() {
    this.dialog?.open(this.sendmailAgainDialog, {
      width: '20vw',
      autoFocus: false
    });
  }

  NoSend() {
    this.dialog.closeAll();
  }
  sendAgainMail() {
    this.loaderService.show();
    this.clientOfferService.sendMailAgainClientOffer(this.editTypeId).subscribe({
      next: (res: any) => {
        if (res["Success"]) {
          this.loaderService.hide();
          this.toaster.success('Mail Send Successfully');
          this.closeDialog();
        } else {
          this.loaderService.hide();
          this.toaster.error('Error');
        }
      },
      error: (_err) => {
        this.loaderService.hide();
        this.toaster.error('Something went wrong Record not Updated sorry');
      }
    });
  }

  loadClientOfferData() {
    this.loaderService.show();
    this.clientOfferService.getClientOffer().subscribe({
      next: (res: any) => {
        if (res["Success"] && res["Data"]) {
          this.clientOfferData = res["Data"].map((item: any) => ({
            ...item,
            PlanType: item.IsMonth ? 'Month' : 'Year'
          }));
          this.columnList.push(
            "Name",
            "Email",
            "PlanName",
            "CreatedOn",
            "PlanType",
            "PlanDuration",
            "Notes",
            "CreatedByName",
            "Address"
          );
          this.loaderService.hide();
          this.updateKendoGridData();
        } else {
          this.loaderService.hide();
        }
      },
      error: (_err) => {
        this.loaderService.hide();
        this.toaster.error('Something went wrong Record not Updated sorry');
      }
    });
  }

  onRowDoubleClick(event: any): void {
    const cell = (event.target as HTMLElement).closest('kendo-grid-list tr');
    if (!cell) return;

    const index = Array.from(cell.parentElement!.children).indexOf(cell);
    const dataItem = this.gridView.data[index];

    if (dataItem) {
      this.openClientOfferDialog(dataItem);
    }
  }

  toggleMaximize(): void {
    const overlayPane = document.querySelector('.cdk-overlay-pane:has(mat-dialog-container)');
    if (!(overlayPane instanceof HTMLElement)) return;
    const dialogElement = document.querySelector('mat-dialog-container');
    if (!(dialogElement instanceof HTMLElement)) return;
    const contentElement = dialogElement?.querySelector('.mat-dialog-content');
    if (!(contentElement instanceof HTMLElement)) return;
    this.mapService.maximizeMinimize();
    if (!overlayPane || !dialogElement) return;
    this.isMaximized = !this.isMaximized;
    if (this.isMaximized) {
      // Save original styles only once
      if (!this.originalOverlayStyle.width) {
        this.originalOverlayStyle = {
          position: overlayPane.style.position,
          top: overlayPane.style.top,
          left: overlayPane.style.left,
          margin: overlayPane.style.margin,
          width: overlayPane.style.width,
          height: overlayPane.style.height,
          maxWidth: overlayPane.style.maxWidth,
          maxHeight: overlayPane.style.maxHeight
        };

        this.originalDialogStyle = {
          width: dialogElement.style.width,
          height: dialogElement.style.height,
          maxHeight: dialogElement.style.maxHeight
        };
      }

      // Apply maximize styles
      overlayPane.style.position = 'fixed';
      overlayPane.style.top = '0';
      overlayPane.style.left = '0';
      overlayPane.style.margin = '0';
      overlayPane.style.setProperty('width', '100vw', 'important');
      overlayPane.style.setProperty('height', '100vh', 'important');
      overlayPane.style.setProperty('max-width', '100vw', 'important');
      overlayPane.style.setProperty('max-height', '100vh', 'important');

      dialogElement.style.setProperty('width', '100vw', 'important');
      dialogElement.style.setProperty('max-width', '100vw', 'important');
      contentElement.style.maxHeight = '72vh';
    } else {
      // Restore original styles
      overlayPane.style.position = this.originalOverlayStyle.position ?? '';
      overlayPane.style.top = this.originalOverlayStyle.top ?? '';
      overlayPane.style.left = this.originalOverlayStyle.left ?? '';
      overlayPane.style.margin = this.originalOverlayStyle.margin ?? '';
      overlayPane.style.width = this.originalOverlayStyle.width ?? '';
      overlayPane.style.height = this.originalOverlayStyle.height ?? '';
      overlayPane.style.maxWidth = this.originalOverlayStyle.maxWidth ?? '';
      overlayPane.style.maxHeight = this.originalOverlayStyle.maxHeight ?? '';

      dialogElement.style.width = this.originalDialogStyle.width ?? '';
      dialogElement.style.height = this.originalDialogStyle.height ?? '';
      contentElement.style.maxHeight = this.originalDialogStyle.maxHeight ?? '';
    }
  }

  onColumnVisibilityChange(columnEvent: GridComponent): void {
    this.gridSettings.saveGridSettings(columnEvent, this.TableName).subscribe();
  }

  ngAfterViewInit(): void {
    this.gridSettings.applyGridSettings(this.api, this.TableName, this.gridKendo);
  }
}
